import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useOvertimeInit,
  useDetectOvertimeType,
  useCalculateOvertimeHours,
  useCreateOvertime,
  useSubmitOvertime,
} from '@saiens/shared/hooks';
import { formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft } from 'lucide-react';
import type { OvertimeApplyRequest } from '@saiens/shared/types';

function getTodayStr(): string {
  const d = new Date();
  return d.toISOString().split('T')[0];
}

export function OvertimeApplyPage() {
  const navigate = useNavigate();
  const { data: initData, isLoading: initLoading } = useOvertimeInit();
  const detectType = useDetectOvertimeType();
  const calculateHours = useCalculateOvertimeHours();
  const createOvertime = useCreateOvertime();
  const submitOvertime = useSubmitOvertime();

  const [overtimeTypeId, setOvertimeTypeId] = useState<number | ''>('');
  const [startDay, setStartDay] = useState(getTodayStr());
  const [hourFrom, setHourFrom] = useState<number | ''>('');
  const [minFrom, setMinFrom] = useState<number | ''>('');
  const [hourTo, setHourTo] = useState<number | ''>('');
  const [minTo, setMinTo] = useState<number | ''>('');
  const [desc, setDesc] = useState('');
  const [calculatedHours, setCalculatedHours] = useState<number | null>(null);
  const [calculatedDisplay, setCalculatedDisplay] = useState('');

  // Set default time values when init data loads
  useEffect(() => {
    if (initData?.hour_options && initData.hour_options.length > 0 && hourFrom === '') {
      const eighteen = initData.hour_options.find((h) => h.value === 18);
      const twentyone = initData.hour_options.find((h) => h.value === 21);
      if (eighteen) setHourFrom(eighteen.value);
      if (twentyone) setHourTo(twentyone.value);
    }
    if (initData?.minute_options && initData.minute_options.length > 0 && minFrom === '') {
      const zero = initData.minute_options.find((m) => m.value === 0);
      if (zero) {
        setMinFrom(zero.value);
        setMinTo(zero.value);
      }
    }
  }, [initData, hourFrom, minFrom]);

  // Auto-detect overtime type when date changes
  useEffect(() => {
    if (!startDay) return;
    detectType.mutate(startDay, {
      onSuccess: (result) => {
        if (result.overtime_type) {
          setOvertimeTypeId(result.overtime_type.id);
        }
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDay]);

  // Debounced calculate hours
  const calcTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (hourFrom === '' || hourTo === '') {
      setCalculatedHours(null);
      setCalculatedDisplay('');
      return;
    }

    clearTimeout(calcTimerRef.current);
    calcTimerRef.current = setTimeout(() => {
      calculateHours.mutate(
        {
          hour_from: Number(hourFrom),
          min_from: Number(minFrom || 0),
          hour_to: Number(hourTo),
          min_to: Number(minTo || 0),
        },
        {
          onSuccess: (result) => {
            setCalculatedHours(result.hours);
            setCalculatedDisplay(result.display_time);
          },
        }
      );
    }, 500);

    return () => clearTimeout(calcTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourFrom, minFrom, hourTo, minTo]);

  const buildPayload = useCallback((): OvertimeApplyRequest => ({
    overtime_type_id: Number(overtimeTypeId),
    start_day: startDay,
    hour_from: Number(hourFrom),
    min_from: Number(minFrom || 0),
    hour_to: Number(hourTo),
    min_to: Number(minTo || 0),
    desc,
  }), [overtimeTypeId, startDay, hourFrom, minFrom, hourTo, minTo, desc]);

  const isSaving = createOvertime.isPending || submitOvertime.isPending;

  const canSubmit =
    overtimeTypeId !== '' &&
    startDay &&
    hourFrom !== '' &&
    hourTo !== '' &&
    !isSaving;

  const handleSaveDraft = useCallback(() => {
    if (!canSubmit) return;
    createOvertime.mutate(buildPayload(), {
      onSuccess: (result) => {
        navigate(`/overtime/${result.id}`, { replace: true });
      },
    });
  }, [canSubmit, buildPayload, createOvertime, navigate]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    createOvertime.mutate(buildPayload(), {
      onSuccess: (result) => {
        submitOvertime.mutate(result.id, {
          onSuccess: () => {
            navigate('/overtime', { replace: true });
          },
        });
      },
    });
  }, [canSubmit, buildPayload, createOvertime, submitOvertime, navigate]);

  if (initLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">加班申請</h1>
      </div>

      <div className="space-y-4 px-4 py-4">
        {/* Date */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">加班日期</label>
          <input
            type="date"
            value={startDay}
            onChange={(e) => setStartDay(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>

        {/* Overtime Type */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">加班類型</label>
          <select
            value={overtimeTypeId}
            onChange={(e) => setOvertimeTypeId(e.target.value ? Number(e.target.value) : '')}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          >
            <option value="">請選擇加班類型</option>
            {initData?.types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Time Selection */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">開始時間</label>
            <div className="flex gap-1">
              <select
                value={hourFrom}
                onChange={(e) => setHourFrom(e.target.value ? Number(e.target.value) : '')}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
              >
                {initData?.hour_options.map((h) => (
                  <option key={h.id} value={h.value}>
                    {h.name}
                  </option>
                ))}
              </select>
              <select
                value={minFrom}
                onChange={(e) => setMinFrom(e.target.value ? Number(e.target.value) : '')}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
              >
                {initData?.minute_options.map((m) => (
                  <option key={m.id} value={m.value}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">結束時間</label>
            <div className="flex gap-1">
              <select
                value={hourTo}
                onChange={(e) => setHourTo(e.target.value ? Number(e.target.value) : '')}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
              >
                {initData?.hour_options.map((h) => (
                  <option key={h.id} value={h.value}>
                    {h.name}
                  </option>
                ))}
              </select>
              <select
                value={minTo}
                onChange={(e) => setMinTo(e.target.value ? Number(e.target.value) : '')}
                className="flex-1 rounded-lg border border-gray-300 bg-white px-2 py-2.5 text-sm focus:border-amber-500 focus:outline-none"
              >
                {initData?.minute_options.map((m) => (
                  <option key={m.id} value={m.value}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Calculated Hours */}
        {calculatedHours != null && (
          <div className="rounded-lg bg-amber-50 p-3">
            <p className="text-xs text-amber-600">預計時數</p>
            <p className="text-lg font-bold text-amber-700">
              {calculatedDisplay || formatWorkHours(calculatedHours)}
            </p>
          </div>
        )}

        {/* Reason */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">加班事由</label>
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            rows={3}
            placeholder="請輸入加班事由"
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pb-4 pt-2">
          <button
            onClick={handleSaveDraft}
            disabled={!canSubmit}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {createOvertime.isPending ? '儲存中...' : '儲存草稿'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 rounded-lg bg-amber-500 py-3 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-40"
          >
            {submitOvertime.isPending ? '送出中...' : '送出申請'}
          </button>
        </div>
      </div>
    </div>
  );
}
